import React from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Box,
  Chip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import type { Location } from '../../types/vrp';
import { LocationActions } from './LocationActions';

interface LocationRowProps {
  location: Location;
  isSelected: boolean;
  onSelect: (locationId: number) => void;
  onView: (location: Location) => void;
  onEdit: (location: Location) => void;
  onDelete: (location: Location) => void;
}

export const LocationRow: React.FC<LocationRowProps> = React.memo(({
  location,
  isSelected,
  onSelect,
  onView,
  onEdit,
  onDelete,
}) => {
  const handleSelectChange = () => {
    onSelect(location.id);
  };

  const handleRowClick = () => {
    onView(location);
  };

  const formatCoordinates = (lat?: number, lon?: number) => {
    if (!lat || !lon) return '-';
    return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
  };

  const getLocationTypeChip = (type?: string) => {
    const isWarehouse = type === 'warehouse';
    return (
      <Chip 
        icon={isWarehouse ? <BusinessIcon /> : <LocationIcon />}
        label={type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Customer'}
        color={isWarehouse ? 'primary' : 'default'}
        size="small"
      />
    );
  };
  return (
    <TableRow 
      hover
      selected={isSelected}
      sx={{ 
        cursor: 'pointer',
        height: 'var(--table-row-height-ultra-dense)',
        '&:hover': {
          backgroundColor: 'var(--vrp-table-hover-bg)',
        },
        '& .MuiTableCell-root': {
          fontSize: 'var(--table-font-size-ultra-dense)',
          lineHeight: 'var(--table-line-height-ultra-dense)',
          padding: 'var(--table-cell-padding-ultra-dense) 8px'
        }
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={handleSelectChange}
          aria-label={`Select ${location.name}`}
          size="small"
        />
      </TableCell>
      
      <TableCell 
        onClick={handleRowClick}
        sx={{ 
          fontWeight: 'medium', 
          color: 'primary.main'
        }}
      >
        <Box display="flex" alignItems="center">
          <LocationIcon sx={{ mr: 0.5, fontSize: 12 }} />
          {location.name}
        </Box>
      </TableCell>
      
      <TableCell>
        {React.cloneElement(getLocationTypeChip(location.location_type), {
          sx: { fontSize: '0.5625rem', height: 16 }
        })}
      </TableCell>
      <TableCell>{location.address || '-'}</TableCell>
      <TableCell>{formatCoordinates(location.location_lat, location.location_lon)}</TableCell>
      <TableCell>{new Date(location.created_at).toLocaleDateString()}</TableCell>
      
      <TableCell align="center">
        <LocationActions
          location={location}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </TableCell>
    </TableRow>
  );
});

LocationRow.displayName = 'LocationRow';
